import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { UserService } from '../../service/user.service';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogService } from '../../../../core/services/dialog.service';
import { AddKidComponent } from '../add-kid/add-kid.component';
import { PARENT_DIALOG_PANEL_CLASS } from '../../../../shared/constants/parent-dialog.constants';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { isParentKycRequiredError } from '../../../identity/helpers/identity-error.helper';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { ParentIdentityService } from '../../../identity/services/parent-identity.service';

type LinkChannel = 'QR' | 'DEEP_LINK' | 'EMAIL' | 'CODE';

@Component({
  selector: 'app-linking-wizard',
  standalone: true,
  imports: [CommonModule, MatDialogModule, TranslatePipe, ReactiveFormsModule, ParentButtonComponent],
  templateUrl: './linking-wizard.component.html',
  styleUrl: './linking-wizard.component.scss',
})
export class LinkingWizardComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<LinkingWizardComponent>);
  #userService = inject(UserService);
  #fb = inject(FormBuilder);
  #dialogService = inject(DialogService);
  #translate = inject(TranslateService);
  #identityNav = inject(IdentityNavigationService);
  #identity = inject(ParentIdentityService);

  data = inject<{ user?: AuthGen.User }>(MAT_DIALOG_DATA);

  step = signal<'choose' | 'existing'>('choose');
  selectedChannel = signal<LinkChannel>('QR');
  generated = signal<AuthGen.ParentLinkRequestResponse | null>(null);
  infoMessage = signal('');

  emailForm = this.#fb.group({
    targetEmail: ['', [Validators.required, Validators.email]],
  });

  isGenerating = computed(() =>
    this.#userService.createParentLinkRequest.isPending(),
  );
  readonly familyBlocked = computed(
    () =>
      this.#identity.layerEnabled() &&
      this.#identity.loaded() &&
      !this.#identity.canExpandFamily(),
  );
  qrImageBase64 = computed(() => this.generated()?.qrImageBase64 ?? '');

  ngOnInit(): void {
    void this.#identity.whenConfigReady();
  }

  discard(): void {
    this.#dialogRef.close();
  }

  async chooseExistingKid(): Promise<void> {
    if (this.familyBlocked()) {
      return;
    }
    this.step.set('existing');
    this.infoMessage.set('');
    this.selectedChannel.set('QR');
    await this.generateInvite();
  }

  chooseCreateKid(): void {
    if (this.familyBlocked()) {
      return;
    }
    this.#dialogService.open(AddKidComponent, {
      data: { user: this.data?.user },
      panelClass: PARENT_DIALOG_PANEL_CLASS,
    });
    this.#dialogRef.close();
  }

  backToChoose(): void {
    this.step.set('choose');
    this.infoMessage.set('');
  }

  setChannel(channel: LinkChannel): void {
    this.selectedChannel.set(channel);
  }

  async generateInvite(): Promise<void> {
    const channel = this.selectedChannel();
    const payload: AuthGen.CreateParentLinkRequestDto = {
      channel,
      targetEmail: '',
      expiresInMinutes: 60,
    };

    if (channel === 'EMAIL') {
      this.emailForm.markAllAsTouched();
      if (this.emailForm.invalid) return;
      payload.targetEmail = this.emailForm.value.targetEmail ?? '';
    }

    try {
      const res =
        await this.#userService.createParentLinkRequest.mutateAsync(payload);
      this.generated.set(res);

      if (channel === 'EMAIL') {
        this.infoMessage.set(
          this.#translate.instant(
            'app.components.linkingWizard.notifications.invitationSent',
          ),
        );
      }
    } catch (error) {
      if (isParentKycRequiredError(error)) {
        this.#dialogRef.close();
        await this.#identityNav.navigateToVerification();
      }
    }
  }

  async shareInvite(): Promise<void> {
    const deepLink = this.generated()?.deepLink;
    const code = this.generated()?.code;
    if (!deepLink && !code) return;
    const qrImage = this.selectedChannel() === 'QR' ? this.qrImageBase64() : '';
    const title = this.#translate.instant(
      'app.components.linkingWizard.share.title',
    );
    const codeText = this.#translate.instant(
      'app.components.linkingWizard.share.codeText',
      { code: code ?? '' },
    );
    const openText = this.#translate.instant(
      'app.components.linkingWizard.share.openText',
      { deepLink: deepLink ?? '' },
    );

    const shareData: ShareData = {
      title,
      text: code
        ? `${codeText}${deepLink ? `\n${openText}` : ''}`
        : openText,
    };

    if (deepLink && !deepLink.startsWith('valvur://')) {
      shareData.url = deepLink;
    }

    if (navigator.share) {
      try {
        if (qrImage) {
          const qrFile = await this.dataUrlToFile(
            qrImage,
            'valvur-family-link-qr.png',
          );

          if (qrFile) {
            const shareDataWithFileOnly: ShareData = {
              files: [qrFile],
            };
            const canShareFileOnly =
              !navigator.canShare || navigator.canShare(shareDataWithFileOnly);

            // Prefer file-only share first for better compatibility with apps like Telegram.
            if (canShareFileOnly) {
              await navigator.share(shareDataWithFileOnly);
              this.infoMessage.set(
                this.#translate.instant(
                  'app.components.linkingWizard.notifications.inviteShared',
                ),
              );
              return;
            }

            const shareDataWithFileAndText: ShareData = {
              ...shareData,
              files: [qrFile],
            };
            const canShareFileAndText =
              !navigator.canShare ||
              navigator.canShare(shareDataWithFileAndText as ShareData);

            if (canShareFileAndText) {
              await navigator.share(shareDataWithFileAndText);
              this.infoMessage.set(
                this.#translate.instant(
                  'app.components.linkingWizard.notifications.inviteShared',
                ),
              );
              return;
            }
          }
        }

        const canShareText =
          !navigator.canShare || navigator.canShare(shareData as ShareData);
        if (canShareText) {
          await navigator.share(shareData);
          this.infoMessage.set(
            this.#translate.instant(
              'app.components.linkingWizard.notifications.inviteShared',
            ),
          );
          return;
        }
      } catch {
        // user canceled or browser-specific error; fallback below
      }
    }

    try {
      const mailSubject = encodeURIComponent(title);
      const mailBody = encodeURIComponent(shareData.text ?? '');
      window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`;
      this.infoMessage.set(
        this.#translate.instant(
          'app.components.linkingWizard.notifications.inviteShared',
        ),
      );
      return;
    } catch {
      // fallback to clipboard below
    }

    const clipboardText = `${shareData.text ?? ''}${
      shareData.url ? `\n${shareData.url}` : ''
    }`;
    await navigator.clipboard?.writeText(clipboardText);
    this.infoMessage.set(
      this.#translate.instant(
        'app.components.linkingWizard.notifications.inviteCopied',
      ),
    );
  }

  startParentVerification(): void {
    this.discard();
    void this.#identityNav.navigateToVerification();
  }

  private async dataUrlToFile(
    dataUrl: string,
    fileName: string,
  ): Promise<File | null> {
    if (!dataUrl || !dataUrl.startsWith('data:')) return null;

    try {
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      return new File([blob], fileName, {
        type: blob.type || 'image/png',
      });
    } catch {
      return null;
    }
  }

}
